import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Card } from '@/components/shadcn/card';
import { Button as ShadcnButton } from '@/components/shadcn/button';
import type { MabarParticipant } from '@/services/communityService';
import padelService, { type PadelRoundDetail } from '@/services/padelService';
import PadelMatchScorePanel from './PadelMatchScorePanel';
import PadelRoundLineupEditor from './PadelRoundLineupEditor';
import { formatPadelGameStatus, playersFirstNameLabel, playersLabel } from './padelUiUtils';
import { validateAmericanoMatchScore } from './padelScoringUtils';

interface PadelRoundCardProps {
  round: PadelRoundDetail;
  canEdit: boolean;
  hideLineupEdit?: boolean;
  participants: MabarParticipant[];
  isSparring?: boolean;
  teamAName?: string;
  teamBName?: string;
  onChanged: () => void;
  onNotify: (message: string, type: 'success' | 'error') => void;
}

const statusBadge: Record<string, string> = {
  completed: 'bg-[#34C759]/15 text-[#34C759]',
  in_progress: 'bg-amber-400/15 text-amber-600',
};

export default function PadelRoundCard({
  round,
  canEdit,
  hideLineupEdit = false,
  participants,
  isSparring = false,
  teamAName = 'Tim A',
  teamBName = 'Tim B',
  onChanged,
  onNotify,
}: PadelRoundCardProps) {
  const navigate = useNavigate();
  const game = round.game;
  const [scoreA, setScoreA] = useState(game?.scoreSideA ?? 0);
  const [scoreB, setScoreB] = useState(game?.scoreSideB ?? 0);
  const [saving, setSaving] = useState(false);
  const [lineupOpen, setLineupOpen] = useState(false);

  if (!game) {
    return (
      <Card className="rounded-xl p-4 text-sm text-gray-500">
        Ronde {round.roundNumber} belum memiliki susunan.
      </Card>
    );
  }

  const labelA = playersLabel(game.sideAPlayers) || 'Tim A';
  const labelB = playersLabel(game.sideBPlayers) || 'Tim B';
  const firstNameLabelA = playersFirstNameLabel(game.sideAPlayers) || 'Tim A';
  const firstNameLabelB = playersFirstNameLabel(game.sideBPlayers) || 'Tim B';
  const editableScore = game.scoringType === 'points' || game.scoringType === 'time';

  const setsFormatted = (game.setScores ?? [])
    .filter((s) => s.isComplete || s.scoreSideA > 0 || s.scoreSideB > 0)
    .map((s) => `${s.scoreSideA}-${s.scoreSideB}`)
    .join(' · ');
  const displayScore =
    game.scoringType === 'sets'
      ? setsFormatted || '0 - 0'
      : `${scoreA} : ${scoreB}`;

  const handleSave = async () => {
    if (editableScore) {
      const americanoErr = validateAmericanoMatchScore(scoreA, scoreB, game.targetScore);
      if (americanoErr) {
        onNotify(americanoErr, 'error');
        return;
      }
    }

    setSaving(true);
    try {
      await padelService.saveScores(game.slug, {
        matchScore: { scoreSideA: scoreA, scoreSideB: scoreB },
      });
      onNotify('Skor ronde disimpan', 'success');
      onChanged();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      onNotify(msg || 'Gagal menyimpan skor', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="overflow-hidden rounded-xl border-l-4 border-l-[#FF5A1F]">
      <div className="flex items-center justify-between gap-2 px-4 pt-4">
        <span className="text-sm font-bold text-gray-900">
          Ronde {round.roundNumber}
          {game.courtNumber != null ? ` · Lap ${game.courtNumber}` : ''}
        </span>
        <span
          className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
            statusBadge[game.status] ?? 'bg-gray-100 text-gray-500'
          }`}
        >
          {formatPadelGameStatus(game.status)}
        </span>
      </div>

      {!canEdit || !editableScore ? (
        <>
          {/* Mobile Section */}
          <div className="flex flex-col gap-2 px-4 py-4 lg:hidden">
            <p className="truncate text-left text-sm font-semibold text-gray-800">
              {firstNameLabelA}
            </p>
            <span className="self-center rounded-md bg-gray-700 px-3 py-1.5 text-sm font-bold text-white">
              {displayScore}
            </span>
            <p className="truncate text-right text-sm font-semibold text-gray-800">
              {firstNameLabelB}
            </p>
          </div>

          {/* Desktop Section */}
          <div className="hidden items-center justify-between gap-3 px-4 py-4 lg:flex">
            <p className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-800">{labelA}</p>
            <span className="flex-shrink-0 rounded-md bg-gray-700 px-3 py-1.5 text-sm font-bold text-white">
              {displayScore}
            </span>
            <p className="min-w-0 flex-1 truncate text-right text-sm font-semibold text-gray-800">
              {labelB}
            </p>
          </div>

          {canEdit && game.scoringType === 'sets' && (
            <div className="flex flex-wrap gap-2 px-4 pb-4">
              <ShadcnButton
                size="sm"
                variant="outline"
                className="gap-1.5 border-[#FF5A1F] text-[#FF5A1F] hover:bg-[#FFF5EE]"
                onClick={() => navigate(`/host/mabar/game/${game.slug}/rekap`)}
              >
                <Icon icon="mdi:pencil-box-outline" className="h-4 w-4" />
                Input Skor Set (Tenis)
              </ShadcnButton>
              {!hideLineupEdit && (
                <ShadcnButton
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => setLineupOpen(true)}
                >
                  <Icon icon="mdi:account-switch" className="h-4 w-4" />
                  Edit Susunan
                </ShadcnButton>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="px-4 py-4">
          <PadelMatchScorePanel
            game={game}
            scoreA={scoreA}
            scoreB={scoreB}
            canEdit={canEdit}
            onChange={(a, b) => {
              setScoreA(a);
              setScoreB(b);
            }}
          />
          <div className="mt-4 flex flex-wrap gap-2">
            <ShadcnButton
              size="sm"
              className="gap-1.5 bg-[#FF5A1F] hover:bg-[#e54e16]"
              onClick={handleSave}
              disabled={saving}
            >
              <Icon icon="mdi:content-save" className="h-4 w-4" />
              {saving ? 'Menyimpan...' : 'Simpan Skor'}
            </ShadcnButton>
            {!hideLineupEdit && (
              <ShadcnButton
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setLineupOpen(true)}
              >
                <Icon icon="mdi:account-switch" className="h-4 w-4" />
                Edit Susunan
              </ShadcnButton>
            )}
          </div>
        </div>
      )}

      {lineupOpen && (
        <PadelRoundLineupEditor
          isOpen={lineupOpen}
          onClose={() => setLineupOpen(false)}
          game={game}
          participants={participants}
          isSparring={isSparring}
          teamAName={teamAName}
          teamBName={teamBName}
          onSuccess={() => {
            setLineupOpen(false);
            onChanged();
          }}
          onError={(m) => onNotify(m, 'error')}
        />
      )}
    </Card>
  );
}
